import axios from "axios";
import BASE_URL from "../../config";

// Get the auth token from localStorage and return headers
const getAuthHeader = () => {
  const tokenData = JSON.parse(localStorage.getItem("authUser") || "{}");
  return {
    Authorization: `Bearer ${tokenData.accessToken}`,
    'Content-Type': 'application/json'
  };
};

// API to fetch bookings with optional filters
export const getBookings = async (filter = {}) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/Booking/get`, filter, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching bookings:", error);
    throw error;
  }
};

// API to insert or update a booking
export const saveBooking = async (bookingData) => {
  try {
    console.log("API call - saveBooking:", bookingData);
    const response = await axios.post(`${BASE_URL}/api/Booking/save`, bookingData, {
      headers: getAuthHeader(),
    });
    console.log("API response - saveBooking:", response.data);
    return response.data;
  } catch (error) {
    console.error("API error - saveBooking:", error);
    throw error;
  }
};

// API to check availability for employees and rooms
export const checkAvailability = async (availabilityData) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/Booking/check-availability`, availabilityData, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error checking availability:", error);
    throw error;
  }
};

// API to check availability for specific time slot
export const checkAvailabilityForTime = async (timeData) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/Booking/check-availability-for-time`, timeData, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error checking time availability:", error);
    throw error;
  }
};

// Get bookings with employees on leave for notifications
// API to fetch customer unbilled services from bookings
export const getCustomerUnbilledServices = async (filter = {}) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/Booking/get-customer-unbilled-services`, filter, {
      headers: getAuthHeader(),
    });
    return response.data?.data || []; // Return the data array from ApiResponse
  } catch (error) {
    console.error("Error fetching customer unbilled services:", error);
    throw error;
  }
};

// API to fetch customer billed standalone services from invoices
export const getCustomerBilledStandaloneServices = async (filter = {}) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/Booking/get-customer-billed-standalone-services`, filter, {
      headers: getAuthHeader(),
    });
    return response.data?.data || []; // Return the data array from ApiResponse
  } catch (error) {
    console.error("Error fetching customer billed standalone services:", error);
    throw error;
  }
};

export const getBookingsWithEmployeesOnLeave = async () => {
  try {
    const response = await axios.post(`${BASE_URL}/api/Booking/get-bookings-with-employees-on-leave`, {}, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching bookings with employees on leave:", error);
    throw error;
  }
};

// API to update completion status of a booking resource
export const updateCompletionStatus = async (bookingResourceDetailId, completionStatus) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/Booking/update-completion-status`, {
      BookingResourceDetail_Id: bookingResourceDetailId,
      CompletionStatus: completionStatus
    }, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error updating completion status:", error);
    throw error;
  }
};

// API to update bulk completion status of all services in a booking
export const updateBulkCompletionStatus = async (bookingId, completionStatus, checkInTime = null) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/Booking/update-bulk-completion-status`, {
      Booking_Id: bookingId,
      CompletionStatus: completionStatus,
      CheckIn_Time: checkInTime
    }, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error updating bulk completion status:", error);
    throw error;
  }
};

// API to update completion status with time tracking
export const updateCompletionStatusWithTime = async (bookingResourceDetailId, completionStatus, checkInTime = null, startTime = null, stopTime = null) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/Booking/update-completion-status`, {
      BookingResourceDetail_Id: bookingResourceDetailId,
      CompletionStatus: completionStatus,
      CheckIn_Time: checkInTime,
      Start_Time: startTime,
      Stop_Time: stopTime
    }, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error updating completion status with time:", error);
    throw error;
  }
};

// Cancel a single service resource immediately (edit form cancel)
export const cancelBookingResource = async (bookingResourceDetailId, cancellationReason) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/Booking/cancel-booking-resource`, {
      BookingResourceDetail_Id: bookingResourceDetailId,
      Cancellation_Reason: cancellationReason
    }, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error cancelling booking resource:", error);
    throw error;
  }
};

// API to update service times when marking as done
export const updateServiceTimesForDone = async (bookingId, serviceTimes) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/Booking/update-service-times-for-done`, {
      Booking_Id: bookingId,
      ServiceTimes: serviceTimes
    }, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error updating service times for done:", error);
    throw error;
  }
};

// API to export bookings to Excel (Consolidated)
export const exportBookingsToExcel = async (filter = {}) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/Booking/export-bookings-excel`, filter, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `BookingReport_${new Date().toISOString().slice(0,19).replace(/[-:]/g, '').replace('T', '_')}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error("Error exporting bookings:", error);
    throw error;
  }
};

// API to export individual booking details to Excel
export const exportBookingDetailsToExcel = async (bookingId) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/Booking/export-details-excel/${bookingId}`, {}, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `BookingDetails_${bookingId}_${new Date().toISOString().slice(0,19).replace(/[-:]/g, '').replace('T', '_')}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error("Error exporting booking details:", error);
    throw error;
  }
};

// API to sign consent for a booking
export const signBookingConsent = async (consentData) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/Booking/sign-consent`, consentData, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error signing consent:", error);
    throw error;
  }
};

// API to reschedule a booking
export const rescheduleBooking = async (rescheduleData) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/Booking/reschedule`, rescheduleData, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error rescheduling booking:", error);
    throw error;
  }
};